import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { ScanFace, Eye, Lock, Globe, FolderSearch, Shield } from 'lucide-react';
import { Modal } from '../UI/Modal';
import { Button } from '../UI/Button';
import { albumsApi } from '../../api/albums';
import { Album } from '../../types';
import s from './FaceRecognitionStep.module.css';
import styles from './EnableFaceRecognitionModal.module.css';

interface EnableFaceRecognitionModalProps {
  album: Album | null;
  isOpen: boolean;
  onClose: () => void;
  onUpdated: (updated: Album) => void;
}

function OptionCard({
  selected,
  onClick,
  icon,
  label,
  description,
  tag,
}: {
  selected: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  description: string;
  tag: string;
}) {
  return (
    <button
      type="button"
      className={[s.option, selected ? s['option-selected'] : ''].filter(Boolean).join(' ')}
      onClick={onClick}
    >
      <div className={[s.radio, selected ? s['radio-selected'] : ''].filter(Boolean).join(' ')}>
        {selected && <div className={s['radio-dot']} />}
      </div>
      <div className={s['option-body']}>
        <div className={s['option-label-row']}>
          {icon}
          <span className={s['option-label']}>{label}</span>
        </div>
        <p className={s['option-desc']}>{description}</p>
        <span className={s['option-tag']}>{tag}</span>
      </div>
    </button>
  );
}

export function EnableFaceRecognitionModal({
  album,
  isOpen,
  onClose,
  onUpdated,
}: EnableFaceRecognitionModalProps) {
  const [visibility, setVisibility] = useState<'all' | 'matched_only'>('all');
  const [scope, setScope] = useState<'album' | 'event'>('album');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen || !album) return;
    setVisibility(album.guestVisibility ?? 'all');
    setScope(album.faceSearchScope ?? 'album');
  }, [isOpen, album]);

  if (!album) return null;

  const handleEnable = async () => {
    setSaving(true);
    try {
      const updated = await albumsApi.update(album._id, {
        faceRecognitionEnabled: true,
        guestVisibility: visibility,
        faceSearchScope: scope,
      });
      onUpdated(updated);
      toast.success('Face recognition enabled — indexing started');
      onClose();
    } catch {
      toast.error('Failed to enable face recognition');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Enable Face Recognition" size="lg">
      <div className={styles['body']}>
        <div className={styles['scroll']}>
          <div className={s.container}>
            {/* Hero summary — reuses the active hero card styling */}
            <div className={[s['hero-card'], s['hero-card-active']].join(' ')}>
              <div className={[s['hero-icon-wrap'], s['hero-icon-wrap-active']].join(' ')}>
                <ScanFace size={22} style={{ color: 'var(--clay-orange)' }} />
              </div>
              <div className={s['hero-body']}>
                <p className={s['hero-title']}>Face match for {album.name}</p>
                <p className={s['hero-subtitle']}>
                  Indexing starts as soon as you enable. Guests can then upload a selfie
                  to instantly find every photo they appear in.
                </p>
              </div>
            </div>

            {/* Guest visibility */}
            <div>
              <p className={s['section-header']}>Guest visibility</p>
              <div className={s.options}>
                <OptionCard
                  selected={visibility === 'all'}
                  onClick={() => setVisibility('all')}
                  icon={<Eye size={15} style={{ color: 'var(--clay-orange)' }} />}
                  label="All photos + My Photos filter"
                  description="Guests browse every photo, plus a &quot;My Photos&quot; tab showing only photos they appear in."
                  tag="Open galleries"
                />
                <OptionCard
                  selected={visibility === 'matched_only'}
                  onClick={() => setVisibility('matched_only')}
                  icon={<Lock size={15} style={{ color: 'var(--clay-orange)' }} />}
                  label="Only their photos + highlights"
                  description="Guests see ONLY photos they appear in, plus any photos you pin as highlights."
                  tag="Personal delivery"
                />
              </div>
            </div>

            {/* Face search scope — only for non-live (curated) albums */}
            {!album.isLive && (
              <div>
                <p className={s['section-header']}>Search scope</p>
                <div className={s.options}>
                  <OptionCard
                    selected={scope === 'album'}
                    onClick={() => setScope('album')}
                    icon={<FolderSearch size={15} style={{ color: 'var(--clay-orange)' }} />}
                    label="This album only"
                    description="Match faces against the photos you selected for this album."
                    tag="Curated albums"
                  />
                  <OptionCard
                    selected={scope === 'event'}
                    onClick={() => setScope('event')}
                    icon={<Globe size={15} style={{ color: 'var(--clay-orange)' }} />}
                    label="All event photos"
                    description="Match faces against every photo from the event — guests may discover additional photos."
                    tag="Full event coverage"
                  />
                </div>
              </div>
            )}

            {/* Privacy callout */}
            <div className={s.callout}>
              <Shield size={16} className={s['callout-icon']} />
              <span>
                Selfies are kept private and only used for matching.
                {visibility === 'matched_only'
                  ? ' Guests must provide a selfie to see their photos.'
                  : ' Guests can skip the selfie and browse all photos.'}
              </span>
            </div>
          </div>
        </div>

        <div className={styles['footer']}>
          <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={handleEnable} loading={saving}>
            <ScanFace size={14} style={{ marginRight: 6 }} />
            Enable & start indexing
          </Button>
        </div>
      </div>
    </Modal>
  );
}
